import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import toast from 'react-hot-toast';
import './Escalations.css';

const formatEventDate = (start, end, isMulti) => {
  if (!start) return '—';
  const s = new Date(start);
  if (!isMulti || !end) return s.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  const e = new Date(end);
  if (s.toDateString() === e.toDateString()) return s.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  
  if (s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear()) {
    return `${s.getDate()} - ${e.getDate()} ${s.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}`;
  }
  return `${s.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} - ${e.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`;
};

export default function Escalations() {
  const { faculty } = useAuth();
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingChat, setLoadingChat] = useState(false);
  const [activeTab, setActiveTab] = useState('active');
  const scrollRef = useRef(null);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const isAdmin = ['iqac', 'super_admin', 'ao'].includes(faculty?.role);
      const res = await api.get(isAdmin ? '/events/all' : '/events');
      // Filter for escalated events and sort them
      const escalated = (res.data.events || [])
        .filter(e => e.isEscalated)
        .sort((a, b) => {
          // If in current tab, sorting logic:
          return new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt);
        });
      setEvents(escalated);
      
      if (escalated.length > 0 && !selectedEvent) {
        // Find first event matching initial active tab
        const firstActive = escalated.find(e => e.escalationStatus === 'active');
        if (firstActive) setSelectedEvent(firstActive);
        else setSelectedEvent(escalated[0]);
      } else if (selectedEvent) {
        const updated = escalated.find(e => e._id === selectedEvent._id);
        if (updated && JSON.stringify(updated) !== JSON.stringify(selectedEvent)) {
          setSelectedEvent(updated);
        }
      }
    } catch (err) {
      toast.error('Failed to load escalations');
    } finally {
      setLoading(false);
    }
  }, [faculty, selectedEvent]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const loadMessages = useCallback(async () => {
    if (!selectedEvent) return;
    setLoadingChat(true);
    try {
      const res = await api.get(`/events/${selectedEvent._id}/messages`);
      setMessages(res.data.messages || []);
    } catch (err) {
      console.error('Failed to load messages:', err);
    } finally {
      setLoadingChat(false);
    }
  }, [selectedEvent]);

  useEffect(() => {
    loadMessages();
    // Poll for new messages and event status every 5 seconds
    const interval = setInterval(() => {
      loadMessages();
      loadEvents();
    }, 5000);
    return () => clearInterval(interval);
  }, [loadMessages, loadEvents]);

  useEffect(() => {
    // Scroll to bottom when messages change
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
    // If an event is selected, mark it as seen
    if (selectedEvent && faculty?._id) {
      const key = `lastSeen_esc_${String(faculty._id)}_${String(selectedEvent._id)}`;
      localStorage.setItem(key, new Date().toISOString());
      window.dispatchEvent(new Event('escalation_seen'));
    }
  }, [messages, selectedEvent, faculty?._id]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedEvent) return;

    const content = newMessage;
    setNewMessage('');

    try {
      const res = await api.post(`/events/${selectedEvent._id}/messages`, { content });
      setMessages(prev => [...prev, res.data.message]);
    } catch (err) {
      toast.error('Failed to send message');
      setNewMessage(content);
    }
  };

  if (loading && events.length === 0) return <div className="page-loading"><div className="spinner" /></div>;

  const filteredList = events.filter(e => e.escalationStatus === activeTab);

  return (
    <div className="page-shell">
      <div className="page-header">
        <div>
          <div className="page-title">Escalations</div>
          <div className="page-subtitle">Resolve event conflicts and manage administrative discussions</div>
        </div>
      </div>

      <div className="page-body" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="escalations-page">
          <div className="escalations-sidebar">
            <div className="sidebar-header-chat">
              <div className="escalation-tabs">
                <button 
                  className={`escalation-tab ${activeTab === 'active' ? 'active' : ''}`}
                  onClick={() => setActiveTab('active')}
                >
                  Active <span>{events.filter(e => e.escalationStatus === 'active').length}</span>
                </button>
                <button 
                  className={`escalation-tab ${activeTab === 'closed' ? 'active' : ''}`}
                  onClick={() => setActiveTab('closed')}
                >
                  Closed <span>{events.filter(e => e.escalationStatus === 'closed').length}</span>
                </button>
              </div>
            </div>
            <div className="escalations-list">
              {filteredList.length === 0 ? (
                <div className="empty-escalations">No {activeTab} discussions</div>
              ) : (
                filteredList.map(ev => (
                  <div 
                    key={ev._id} 
                    className={`escalation-item ${selectedEvent?._id === ev._id ? 'active' : ''}`}
                    onClick={() => setSelectedEvent(ev)}
                  >
                    <div className="escalation-item-title">{ev.title}</div>
                    <div className="escalation-item-meta">
                      <span>{ev.venue}</span>
                      <span className="dot">·</span>
                      <span style={{ fontWeight: 700, color: '#005fa7' }}>{formatEventDate(ev.date, ev.endDate, ev.isMultiDay)}</span>
                    </div>
                    <div className={`escalation-badge ${ev.escalationStatus === 'closed' ? 'closed' : ''}`}>
                      {ev.escalationStatus === 'closed' ? 'Closed' : 'Action Required'}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="chat-container">
            {selectedEvent ? (
              <>
                <div className="chat-header">
                  <div className="chat-header-info">
                    <h3>{selectedEvent.title}</h3>
                    <div className="chat-header-meta">
                      {selectedEvent.venue} · Hosted by {selectedEvent.faculty?.name || 'Faculty'} · <span style={{ fontWeight: 800, color: '#005fa7' }}>{formatEventDate(selectedEvent.date, selectedEvent.endDate, selectedEvent.isMultiDay)}</span>
                    </div>
                  </div>
                  <div className="chat-header-actions">
                    {selectedEvent.escalationReason && (
                      <div className="escalation-reason-box">
                        <strong>Reason: </strong>{selectedEvent.escalationReason}
                      </div>
                    )}
                    {selectedEvent.escalationStatus === 'closed' && (
                      <div className="escalation-status-pill closed">Resolved</div>
                    )}
                  </div>
                </div>

                <div className="chat-messages" ref={scrollRef}>
                  {loadingChat && messages.length === 0 ? (
                    <div className="chat-loading"><div className="spinner" /></div>
                  ) : messages.length === 0 ? (
                    <div className="chat-empty">Start a discussion to resolve the escalation.</div>
                  ) : (
                    messages.map((msg, idx) => {
                      if (msg.isSystem) {
                        return (
                          <div key={msg._id} className="message-system">
                            <div className="message-system-content">{msg.content}</div>
                            <div className="message-system-time">
                              {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </div>
                          </div>
                        );
                      }
                      const isMe = String(msg.sender?._id) === String(faculty?._id);
                      const showSender = idx === 0 || messages[idx-1].sender?._id !== msg.sender?._id;
                      
                      return (
                        <div key={msg._id} className={`message-wrapper ${isMe ? 'me' : 'them'}`}>
                          {showSender && !isMe && (
                            <div className="message-sender">
                              {msg.sender?.name} <span className="sender-role">({msg.sender?.role})</span>
                            </div>
                          )}
                          <div className="message-bubble">
                            {msg.content}
                            <div className="message-time">
                              {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {selectedEvent.escalationStatus !== 'closed' ? (
                  <form className="chat-input-area" onSubmit={handleSendMessage}>
                    <input 
                      type="text" 
                      placeholder="Type your message..." 
                      value={newMessage}
                      onChange={e => setNewMessage(e.target.value)}
                    />
                    <button type="submit" className="btn btn-navy" disabled={!newMessage.trim()}>Send</button>
                  </form>
                ) : (
                  <div className="chat-closed-notice">
                    This escalation has been resolved. Further messages are disabled.
                  </div>
                )}
              </>
            ) : (
              <div className="chat-placeholder">
                <div className="placeholder-icon">💬</div>
                <h3>Select an escalation</h3>
                <p>Pick an event from the sidebar to view the discussion</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
